/** Anything with an optional router path: a menu entry, whatever its type. */
export interface RoutedEntry {
    route?: string;
}

/**
 * How the groups of the menu behave, as the `defaults` of the sidebar-menu ui_config sets them for every group that does not say
 * otherwise. Every key is optional: a missing one falls back to the built-in value of `DEFAULT_MENU_OPTIONS`.
 */
export interface MenuDefaults {
    /** The groups can be opened and closed (accordion). When false they are always open, under a plain label. */
    collapsible?: boolean;
    /** A collapsible group starts open (true) or closed (false). The user can still toggle it. */
    expanded?: boolean;
    /** The group that holds the page being shown opens by itself, on load and at every navigation. */
    openActiveSection?: boolean;
}

/** What a menu without any configuration does: accordions, closed, the one of the current page open. */
export const DEFAULT_MENU_OPTIONS: Readonly<Required<MenuDefaults>> = { collapsible: true, expanded: false, openActiveSection: true };

/** A menu entry as the sidebar-menu ui_config carries it (structural twin of `MenuItem` in core, which shared code must not import). */
export interface MenuEntry extends RoutedEntry {
    type: 'item' | 'submenu' | 'divider' | 'group';
    label?: string;
    icon?: string;
    children?: MenuEntry[];
    /** On a 'group' or 'submenu': overrides `MenuDefaults.collapsible` for this group. */
    collapsible?: boolean;
    /** On a 'group' or 'submenu': overrides `MenuDefaults.expanded` for this group. */
    expanded?: boolean;
}

/** One group of the menu: a header and the entries under it. */
export interface MenuSection {
    kind: 'section';
    label: string;
    icon?: string;
    entries: MenuEntry[];
    /** The group's own setting, when it has one (else the menu's `defaults`, else the built-in value). */
    collapsible?: boolean;
    expanded?: boolean;
}

/** What the sidebar draws, after the flat list of the ui_config has been grouped. */
export type MenuNode =
    | { kind: 'divider' }
    | { kind: 'entry'; item: MenuEntry }
    | MenuSection;

/** The `collapsible` / `expanded` a group declares, and only those: a section carries no key it was not given. */
function declaredOptions(item: MenuEntry): { collapsible?: boolean; expanded?: boolean } {
    return {
        ...(item.collapsible !== undefined && { collapsible: item.collapsible }),
        ...(item.expanded !== undefined && { expanded: item.expanded }),
    };
}

/**
 * Groups the entries of the menu into collapsible sections, whichever way the ui_config spells a group:
 * - a `submenu` is a section holding its `children`;
 * - a `group` (a header) is a section holding the `item`s that follow it, up to the next `group`, `submenu` or `divider`;
 * - a `divider` ends the section being filled and is kept as a separator;
 * - an `item` that follows no header stays a plain entry.
 * A header with nothing under it is still a section (with no entries). Anything else is ignored.
 */
export function buildMenuSections(items: readonly MenuEntry[]): MenuNode[] {
    const nodes: MenuNode[] = [];
    let open: MenuSection | null = null;

    for (const item of items) {
        switch (item.type) {
            case 'divider':
                nodes.push({ kind: 'divider' });
                open = null;
                break;
            case 'group':
                open = { kind: 'section', label: item.label ?? '', icon: item.icon, entries: [], ...declaredOptions(item) };
                nodes.push(open);
                break;
            case 'submenu':
                nodes.push({
                    kind: 'section', label: item.label ?? '', icon: item.icon,
                    entries: (item.children ?? []).filter(child => child.type === 'item'),
                    ...declaredOptions(item),
                });
                open = null;
                break;
            case 'item':
                if (open) {
                    open.entries.push(item);
                } else {
                    nodes.push({ kind: 'entry', item });
                }
                break;
        }
    }
    return nodes;
}

/**
 * Whether a section can be opened and closed, and whether it starts open. Precedence, most specific first: the setting of the
 * group itself, the `defaults` of the menu, the built-in `DEFAULT_MENU_OPTIONS`. A group that is not collapsible is always open.
 */
export function resolveSectionOptions(
    section: { collapsible?: boolean; expanded?: boolean },
    defaults?: MenuDefaults,
): { collapsible: boolean; expanded: boolean } {
    const collapsible = section.collapsible ?? defaults?.collapsible ?? DEFAULT_MENU_OPTIONS.collapsible;
    const expanded = section.expanded ?? defaults?.expanded ?? DEFAULT_MENU_OPTIONS.expanded;
    return { collapsible, expanded: collapsible ? expanded : true };
}

/** Whether the group of the page being shown opens by itself (`defaults.openActiveSection`, built-in: yes). */
export function opensActiveSection(defaults?: MenuDefaults): boolean {
    return defaults?.openActiveSection ?? DEFAULT_MENU_OPTIONS.openActiveSection;
}

/** The glyph of a section: its own, else the one of its first entry that has one (a header carries none), else a folder. */
export function sectionIcon(section: { icon?: string; entries: readonly MenuEntry[] }): string {
    return section.icon ?? section.entries.find(e => e.icon)?.icon ?? 'folder';
}

/** The router URL without query string and fragment (`/contract/person/1?tab=a#x` -> `/contract/person/1`). */
export function pathOf(url: string): string {
    return url.split(/[?#]/, 1)[0];
}

/**
 * True when `url` is the page of `route` or one below it: `/contract/person` matches `/contract/person` and
 * `/contract/person/42` (its detail), but not `/contract/person-extra`. A missing or empty route never matches.
 */
export function isUrlUnderRoute(url: string, route: string | undefined): boolean {
    if (!route) {
        return false;
    }
    const path = pathOf(url);
    const base = route.length > 1 && route.endsWith('/') ? route.slice(0, -1) : route;
    return path === base || path.startsWith(base + '/');
}

/** True when the current `url` belongs to one of the entries of a menu group (used to open, and highlight, that group). */
export function isUrlInGroup(url: string, children: readonly RoutedEntry[] | undefined): boolean {
    return (children ?? []).some(child => isUrlUnderRoute(url, child.route));
}
